import { z } from 'zod';
import { requiredFieldError, requiredNumberString, requiredString } from '../../helper';
import { EnterpriseStructure } from '../../types';
import { address } from './address';

const baseBusinessDetails = z.object({
  structure: z.nativeEnum(EnterpriseStructure),
  legalEntityName: requiredString('Legal business name').max(255, 'Legal business name must be at most 255 characters'),
  registeredAddress: address,
});

export const contactDetails = z.object({
  firstName: requiredString('First name').max(255, 'First name must be at most 255 characters'),
  middleName: z.string().trim().max(255, 'Middle name must be at most 255 characters').nullish(),
  lastName: requiredString('Last name').max(255, 'Last name must be at most 255 characters'),
  residentialAddress: address,
});

export const billingContactDetails = contactDetails;

const extendedBusinessFields = {
  legalEntityName: requiredString('Legal business name').max(255, 'Legal business name must be at most 255 characters'),
  registeredAddress: address.optional(),
  tradingName: requiredString('Trading name').max(255, 'Trading name must be at most 255 characters'),
  abn: requiredString('ABN')
    .length(11, 'ABN must be exactly 11 digits')
    .regex(/^\d+$/, 'ABN must contain only numbers'),
  businessPhone: requiredString('Business phone')
    .min(10, 'Please enter a valid Australian phone number')
    .max(10, 'Please enter a valid Australian phone number')
    .regex(/^\d+$/, 'Please enter a valid Australian phone number')
    .regex(/^0\d/, 'Please enter a valid Australian phone number'),
  businessEmail: requiredString('Business email')
    .max(255, 'Business email must be at most 255 characters')
    .email('Please enter a valid email address'),
  businessWebsite: z.string().trim().max(255, 'Business website must be at most 255 characters').optional(),
  yearsInTrading: requiredString('Years in trading'),
  numberOfEmployees: requiredString('Number of employees'),
  industryCategory: requiredString('Industry category'),
  propertyStatus: requiredString('Status of property where business resides'),
  primaryContact: contactDetails,
  billingContactSameAsPrimary: z.boolean(),
  billingContact: billingContactDetails.optional(),
};

export const companyBusinessDetails = baseBusinessDetails.extend({
  structure: z.literal(EnterpriseStructure.Company),
  principalBusinessAddress: address,
  acnNumber: requiredString(),
});

export const proprietaryLimitedCompanyBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.ProprietaryLimitedCompany),
  ...extendedBusinessFields,
});

export const associationBusinessDetails = baseBusinessDetails.extend({
  structure: z.enum([EnterpriseStructure.IncorporatedAssociation, EnterpriseStructure.UnincorporatedAssociation]),
  associationNumber: requiredNumberString(),
});

export const partnershipBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.Partnership),
  ...extendedBusinessFields,
});

export const trustBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.Trust),
  ...extendedBusinessFields,
  trustDeedFiles: z.array(z.any()).optional(),
  trustDeedDocumentIds: z.array(z.string()).optional(),
  trustDeedDocumentNames: z.array(z.string()).optional(),
});

export const soleTraderBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.SoleTrader),
  ...extendedBusinessFields,
});

export const businessDetails = z.discriminatedUnion(
  'structure',
  [
    companyBusinessDetails,
    proprietaryLimitedCompanyBusinessDetails,
    associationBusinessDetails,
    partnershipBusinessDetails,
    trustBusinessDetails,
    soleTraderBusinessDetails,
  ],
  {
    errorMap: () => ({
      message: requiredFieldError('Entity Structure'),
    }),
  },
);
